/**
 * PDFIndexPage.tsx — Consolidated index of every PDF deliverable
 *
 * Catalogues every native-PDF generator in the tool with a short description,
 * source page, target audience, contents summary and CONFIDENTIAL flag.
 */

import { useNavigate } from 'react-router';
import { FileText, ArrowLeft, ExternalLink, ShieldAlert } from 'lucide-react';
import PageHeader from '@/components/PageHeader';

interface PDFEntry {
  id: string;
  title: string;
  sourcePage: string;
  sourcePath: string;
  audience: string;
  contents: string;
  confidential: boolean;
  status: 'live' | 'wip';
}

const PDFS: PDFEntry[] = [
  { id: 'calc', title: 'Calculation Steps Report', sourcePage: 'Reservoir Calculation Steps', sourcePath: '/reservoir-steps', audience: 'Design team, technical reviewer', contents: 'Full step-by-step calculation chain from raw inputs through diffuser physics, plume dynamics, AOTR demand and module count, every step cited.', confidential: true, status: 'live' },
  { id: 'sediment', title: 'Sediment Bed Physics Report', sourcePage: 'Sediment Bed Physics', sourcePath: '/sediment-bed-physics', audience: 'Design team, sediment engineer', contents: 'Bed-type physics across fine silt / medium silt / fine sand for multiple depth points; Shields, resuspension, erosion, SOD enhancement.', confidential: true, status: 'live' },
  { id: 'pre-dredge', title: 'Pre-Dredge Assessment Report', sourcePage: 'Sediment Bed Physics', sourcePath: '/sediment-bed-physics', audience: 'Client, environmental lead', contents: 'Baseline reservoir condition with current silt accumulation; identifies risk zones.', confidential: true, status: 'live' },
  { id: 'post-dredge', title: 'Post-Dredge Assessment Report', sourcePage: 'Sediment Bed Physics', sourcePath: '/sediment-bed-physics', audience: 'Client, environmental lead', contents: 'Restored-bed condition; benchmark against pre-dredge.', confidential: true, status: 'live' },
  { id: 'layout', title: 'Reservoir Layout Report', sourcePage: 'Reservoir Layout', sourcePath: '/reservoir-layout', audience: 'Design team, contractor', contents: 'Layout plan with array zones, module positions, exclusion zones and coverage maps.', confidential: true, status: 'live' },
  { id: 'module-array', title: 'Module Array Report', sourcePage: 'Module Array', sourcePath: '/module-array', audience: 'Design team, contractor', contents: 'Module-level diffuser arrangement, mounting, ballasting, hydraulic details.', confidential: true, status: 'live' },
  { id: 'validation', title: 'Reservoir Validation Report', sourcePage: 'Reservoir Validation', sourcePath: '/reservoir-validation', audience: 'Technical reviewer, regulator', contents: 'Validation against literature bands and limits across every governing physics regime.', confidential: true, status: 'live' },
  { id: 'diff-validation', title: 'Diffuser Validation Report', sourcePage: 'Diffuser Validation', sourcePath: '/diffuser-validation', audience: 'Technical reviewer, supplier', contents: 'Validation of the chosen diffuser model against literature, including bubble Ø, SOTE, shear, plume coverage.', confidential: true, status: 'live' },
  { id: 'master', title: 'MASTER Reservoir Report', sourcePage: 'Master Report', sourcePath: '/reservoir-master', audience: 'Atkins Réalis, South East Water, regulator', contents: 'Consolidated 10-part design dossier covering every page of analysis, including Part J advanced physics and Part H.2 lifecycle economics.', confidential: true, status: 'live' },
  { id: 'comparison', title: 'Diffuser Comparison Matrix', sourcePage: 'Diffuser Comparison', sourcePath: '/diffuser-comparison', audience: 'Atkins Réalis, decision panel', contents: 'Side-by-side scored comparison of all candidate diffusers against the active session.', confidential: true, status: 'live' },
  { id: 'risk', title: 'Risk Register', sourcePage: 'Risk Register', sourcePath: '/risk-register', audience: 'Atkins Réalis, SEW, safety reviewer', contents: 'Engineering, safety, environmental, commercial, regulatory and operational risks with controls and residual scoring.', confidential: true, status: 'live' },
  { id: 'site', title: 'Site Visit Data Capture', sourcePage: 'Site Visit Form', sourcePath: '/site-data-capture', audience: 'Internal site team', contents: 'Pre/post-award field record across bathymetry, sediment cores, water chemistry, hydrology, power, acoustics, biosecurity and pilot siting.', confidential: true, status: 'live' },
  { id: 'pitch', title: 'Pre-Award Technical Pitch', sourcePage: 'Generate from any page', sourcePath: '/reservoir-master', audience: 'Atkins Réalis, Harrison Lewis', contents: '8-section formal pitch covering position, physics regimes, calculation variants, pre-selection programme, compliance activities, why each matters, delivery platform.', confidential: true, status: 'live' },
];

export default function PDFIndexPage() {
  const navigate = useNavigate();

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={FileText}
        title="PDF Deliverables — Master Index"
        subtitle="Every native-PDF report the tool can generate, with audience, contents and source page"
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Total deliverables</div>
          <div className="text-3xl font-bold text-slate-100 mt-1">{PDFS.length}</div>
        </div>
        <div className="rounded-xl border border-emerald-700 bg-emerald-900/20 p-4">
          <div className="text-xs text-emerald-300 uppercase tracking-wider">Live generators</div>
          <div className="text-3xl font-bold text-emerald-200 mt-1">{PDFS.filter(p => p.status === 'live').length}</div>
        </div>
        <div className="rounded-xl border border-amber-700 bg-amber-900/20 p-4">
          <div className="text-xs text-amber-300 uppercase tracking-wider">CONFIDENTIAL</div>
          <div className="text-3xl font-bold text-amber-200 mt-1">{PDFS.filter(p => p.confidential).length}</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Generated since</div>
          <div className="text-lg font-bold text-slate-100 mt-1">v1.0</div>
          <div className="text-xs text-slate-500 mt-1">Current build: v51</div>
        </div>
      </div>

      <div className="space-y-2">
        {PDFS.map(p => (
          <div key={p.id} className="rounded-xl border border-slate-700 bg-slate-900 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-bold text-slate-100">{p.title}</div>
                  {p.confidential && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-900/30 text-red-300 uppercase tracking-wider">
                      <ShieldAlert className="w-3 h-3" /> Confidential
                    </span>
                  )}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider ${
                    p.status === 'live' ? 'bg-emerald-900/30 text-emerald-300' : 'bg-amber-900/30 text-amber-300'
                  }`}>{p.status}</span>
                </div>
                <div className="text-xs text-slate-400 mt-1">{p.contents}</div>
                <div className="text-xs text-slate-500 mt-2">
                  Source: <span className="text-slate-300">{p.sourcePage}</span> · Audience: <span className="text-slate-300">{p.audience}</span>
                </div>
              </div>
              <button onClick={() => navigate(p.sourcePath)}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-700 shrink-0">
                <ExternalLink className="w-3.5 h-3.5" /> Open source
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Cross-deliverable conventions</div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-1">
          <p>• Every PDF carries a diagonal CONFIDENTIAL watermark.</p>
          <p>• Every PDF embeds full metadata (Title, Author, Subject, Keywords, Creator) for archive traceability.</p>
          <p>• Every PDF uses native vector text — selectable, searchable, copyable — not screenshots.</p>
          <p>• Every PDF carries a unique report reference (e.g. PITCH-123456) in header and footer.</p>
          <p>• Every PDF uses British English throughout.</p>
        </div>
      </div>
    </div>
  );
}
